import { useRef, useState } from 'react'

/**
 * Cursor-tracking hover state shared by every chart so they all behave
 * identically. Returns:
 *   - `ref`    — attach to the chart's positioned (`relative`) wrapper.
 *   - `hover`  — the current hover payload `{ ...data, x, y }` or null.
 *   - `track`  — `track(payload)` returns a mouse handler; spread it onto
 *                each datapoint's `onMouseEnter` + `onMouseMove`.
 *   - `clear`  — set on each datapoint's `onMouseLeave`.
 *
 * Pair with <ChartTooltip {...hover} bounds={wrapperWidth} />. The x/y are
 * pointer coordinates relative to the wrapper, so the tooltip positions
 * itself the same way across donuts, bars, columns and sparklines.
 */
export function useChartHover() {
  const ref = useRef(null)
  const [hover, setHover] = useState(null)
  const track = (payload) => (e) => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    // `x/y/w` remain wrapper-relative for backwards-compat with the
    // absolute-positioned fallback in ChartTooltip. `clientX/clientY`
    // are viewport coordinates — ChartTooltip prefers them so the
    // tooltip can render in `position: fixed` and escape any parent
    // overflow / narrow wrapper clipping (e.g. the Usage column on
    // the platform Storage table, where the QuotaBar wrapper is
    // narrower than the tooltip content).
    setHover({
      ...payload,
      x: e.clientX - r.left,
      y: e.clientY - r.top,
      w: r.width,
      clientX: e.clientX,
      clientY: e.clientY,
    })
  }
  const clear = () => setHover(null)
  return { ref, hover, track, clear }
}
